import { useLayoutEffect, useRef, useState } from 'react';

/**
 * SVG line segment overlay, with optional arrowhead.
 * Coordinates are in data-space; axis maps them to [0,1]x[0,1].
 * @param {{ axis, x1, x2, y1, y2, color, dashArray, strokeWidth, showRegion, arrowStyle, arrowWidth }} props
 */
export default function IntFigLine({
  axis,
  x1: lx1, x2: lx2, y1: ly1, y2: ly2,
  color = '#7fffd4',
  dashArray = '0 0',
  strokeWidth = 0.02,
  showRegion = false,
  arrowStyle = 1,
  arrowWidth = 1.0,
}) {
  const svgRef = useRef(null);
  const [aspect, setAspect] = useState(1);

  useLayoutEffect(() => {
    if (!svgRef.current) return;
    const { width, height } = svgRef.current.getBoundingClientRect();
    if (height > 0) setAspect(width / height);
  }, []);

  /* Map data-space → [0,1] */
  const x1 = (lx1 - axis.min_x) / (axis.max_x - axis.min_x);
  const x2 = (lx2 - axis.min_x) / (axis.max_x - axis.min_x);
  const y1 = 1 - (ly1 - axis.min_y) / (axis.max_y - axis.min_y);
  const y2 = 1 - (ly2 - axis.min_y) / (axis.max_y - axis.min_y);

  const svgClass = showRegion ? 'edcomp-intfig-line-region' : 'edcomp-intfig-line';

  if (arrowStyle !== 1) {
    return (
      <svg ref={svgRef} className={svgClass} height="100%" width="100%"
        viewBox="0 0 1 1" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0 }}>
        <line x1={x1} y1={y1} x2={x2} y2={y2}
          stroke={color} strokeWidth={strokeWidth} strokeDasharray={dashArray} />
      </svg>
    );
  }

  const angle     = Math.atan2(y2 - y1, x2 - x1);
  const propScale = Math.abs(Math.sin(angle));
  const scaleFactor = aspect * propScale + (1 - propScale);
  const scaleText = `scale(${scaleFactor},1) rotate(180,0.125,0)`;
  const arrowId = `intfig-arrow-${x1.toFixed(3)}-${y1.toFixed(3)}`;

  return (
    <svg ref={svgRef} className={svgClass} height="100%" width="100%"
      viewBox="0 0 1 1" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0 }}>
      <defs>
        <marker
          id={arrowId}
          overflow="visible"
          refX="0" refY="0"
          orient="auto-start-reverse"
          markerWidth="2" markerHeight="2"
          viewBox="0 0 1 1"
          preserveAspectRatio="xMidYMid"
        >
          <path
            fill="none"
            stroke={color}
            strokeWidth={arrowWidth * strokeWidth * 50 / scaleFactor}
            strokeLinecap="butt"
            d="M 2,-1.5 0,0 2,1.5"
            transform={scaleText}
          />
        </marker>
      </defs>
      <line
        x1={x1} y1={y1} x2={x2} y2={y2}
        stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={dashArray}
        markerEnd={`url(#${arrowId})`}
      />
    </svg>
  );
}
